import React, { Suspense, lazy, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Layout from './layouts/Layout';
import Onboarding from './components/Onboarding';
import { ToastProvider } from './components/Toast';
import { ThemeProvider } from './themes';
import { useStore } from './store/useStore';
import { LiveFeed } from './components/LiveFeed';

const Feed = lazy(() => import('./pages/Feed'));
const MapView = lazy(() => import('./pages/MapView'));
const Home = lazy(() => import('./pages/Home'));
const Create = lazy(() => import('./pages/Create'));
const Profile = lazy(() => import('./pages/Profile'));
const Trails = lazy(() => import('./pages/Trails'));
const Login = lazy(() => import('./pages/Login'));

function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50 dark:bg-neutral-950">
      <div className="w-9 h-9 border-4 border-brand-600 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function App() {
  const { user, darkMode, hasSeenOnboarding, setHasSeenOnboarding, setUser } = useStore();
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    // Check for demo auth persistence
    if (!user && localStorage.getItem('demo_auth') === 'true') {
      setUser({
        id: 'demo-user',
        email: 'demo@example.com',
        name: 'Demo User'
      });
    }
  }, [user, setUser]);

  useEffect(() => {
    // Apply dark mode class
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  // Show onboarding for new users
  useEffect(() => {
    if (user && !hasSeenOnboarding) {
      setShowOnboarding(true);
    }
  }, [hasSeenOnboarding, user]);

  const handleOnboardingComplete = () => {
    setHasSeenOnboarding(true);
    setShowOnboarding(false);
  };

  return (
    <ThemeProvider>
      <ToastProvider>
        {showOnboarding && <Onboarding onComplete={handleOnboardingComplete} />}
        <Router>
          <Suspense fallback={<LoadingScreen />}>
            <Routes>
              <Route path="/login" element={!user ? <Login /> : <Navigate to="/" replace />} />
              
              <Route path="/" element={user ? <Layout /> : <Navigate to="/login" replace />}>
                <Route index element={<Feed />} />
                <Route path="map" element={<MapView />} />
                <Route path="my-shifts" element={<Home />} />
                <Route path="create" element={<Create />} />
                <Route path="profile" element={<Profile />} />
                <Route path="trails" element={<Trails />} />
              </Route>
            </Routes>
            {user && <LiveFeed />}
          </Suspense>
        </Router>
      </ToastProvider>
    </ThemeProvider>
  );
}

export default App;
